import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { getContext } from '../../../state/modules/aiAssistant/selectors';
import MessageRenderer from './MessageRenderer';

const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 19V5M5 12l7-7 7 7" />
  </svg>
);

const ChatInterface = ({ messages, isTyping, error, onSendMessage }) => {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const context = useSelector(getContext);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!inputValue.trim()) {
      return;
    }

    onSendMessage(inputValue.trim());
    setInputValue('');

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleInput = (e) => {
    setInputValue(e.target.value);

    e.target.style.height = 'auto';
    const newHeight = Math.min(e.target.scrollHeight, 120);
    e.target.style.height = `${newHeight}px`;
  };

  const getAvailableActions = () => {
    const pageType = context?.pageType || 'general';

    const actions = {
      global: [
        { label: 'Create a new course', prompt: 'Create a new course about ', icon: '📚' },
        { label: 'Go to my courses', prompt: 'Take me to my courses', icon: '🎓' },
        { label: 'Navigate to dashboard', prompt: 'Go to dashboard', icon: '📊' },
        { label: 'Open store settings', prompt: 'Take me to store settings', icon: '🏪' },
        { label: 'View my members', prompt: 'Show me my members', icon: '👥' },
        { label: 'Get platform help', prompt: 'How do I ', icon: '❓' }
      ],
      store: [
        { label: 'Update brand name', prompt: 'Update my brand name to ', icon: '🏷️' },
        { label: 'Improve tagline', prompt: 'Improve my store tagline', icon: '✨' },
        { label: 'Enhance about text', prompt: 'Enhance my about text', icon: '📝' },
        { label: 'Go to my courses', prompt: 'Take me to my courses', icon: '🎓' },
        { label: 'View analytics', prompt: 'Show me my analytics', icon: '📈' },
        { label: 'Open media library', prompt: 'Take me to media library', icon: '🎬' }
      ],
      course: [
        { label: 'Add sections to course', prompt: 'Add sections to my course about ', icon: '📑' },
        { label: 'Create lesson outline', prompt: 'Create a lesson outline for ', icon: '📖' },
        { label: 'Generate quiz questions', prompt: 'Generate quiz questions about ', icon: '❓' },
        { label: 'Go to all courses', prompt: 'Show me all my courses', icon: '📚' },
        { label: 'View store settings', prompt: 'Take me to store settings', icon: '🏪' },
        { label: 'Check members', prompt: 'Show my members', icon: '👥' }
      ],
      dashboard: [
        { label: 'View my courses', prompt: 'Take me to my courses', icon: '📚' },
        { label: 'Create new course', prompt: 'I want to create a course', icon: '➕' },
        { label: 'Go to members', prompt: 'Show me my members', icon: '👥' },
        { label: 'Open store settings', prompt: 'Take me to store settings', icon: '🏪' },
        { label: 'View emails', prompt: 'Show my emails', icon: '📧' },
        { label: 'Check automations', prompt: 'Take me to automations', icon: '⚙️' }
      ]
    };

    const pageActions = actions[pageType] || [];
    return pageActions.length > 0 ? pageActions : actions.global;
  };

  const renderWelcomeMessage = () => {
    const availableActions = getAvailableActions();

    return (
      <div className="ai-assistant-sidebar__empty-state">
        <p className="ai-assistant-sidebar__empty-text">
          Hi! I can help you with:
        </p>

        <div className="ai-assistant-sidebar__actions-list">
          {availableActions.slice(0, 6).map((item, index) => (
            <button
              key={index}
              className="ai-assistant-sidebar__action-item"
              onClick={() => onSendMessage(item.prompt)}
            >
              <span className="ai-assistant-sidebar__action-icon">{item.icon}</span>
              <span className="ai-assistant-sidebar__action-text">{item.label}</span>
            </button>
          ))}
        </div>

        <p className="ai-assistant-sidebar__help-text">
          Click an action above or type your own question
        </p>
      </div>
    );
  };

  return (
    <div className="ai-assistant-sidebar__chat">
      <div className="ai-assistant-sidebar__messages">
        {messages.length === 0 && renderWelcomeMessage()}

        {messages.map((message) => (
          <MessageRenderer key={message.id} message={message} />
        ))}

        {isTyping && (
          <div className="ai-assistant-sidebar__typing">
            <span className="ai-assistant-sidebar__typing-dot" />
            <span className="ai-assistant-sidebar__typing-dot" />
            <span className="ai-assistant-sidebar__typing-dot" />
          </div>
        )}

        {error && (
          <div className="ai-assistant-sidebar__error">
            <span>{error}</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="ai-assistant-sidebar__input-form">
        <form className="ai-assistant-sidebar__composer-box" onSubmit={handleSubmit}>
          <textarea
            ref={textareaRef}
            className="ai-assistant-sidebar__input"
            value={inputValue}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            placeholder="Ask me anything…"
            rows={1}
          />
          <button
            type="submit"
            className="ai-assistant-sidebar__send-btn"
            disabled={!inputValue.trim() || isTyping}
          >
            <SendIcon />
          </button>
        </form>
        <div className="ai-assistant-sidebar__composer-hint">
          AI Assistant · powered by Miestro
        </div>
      </div>
    </div>
  );
};

ChatInterface.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
      content: PropTypes.string.isRequired,
      timestamp: PropTypes.string.isRequired
    })
  ).isRequired,
  isTyping: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onSendMessage: PropTypes.func.isRequired
};

export default ChatInterface;
